/**
 * Shrink photos before upload: phone cameras produce 12+ megapixel images of several MB
 * that load slowly (or not at all on a weak connection) and are far larger than any chat
 * view needs. Done in the browser so it also covers end-to-end encrypted DMs, where the
 * server only ever sees ciphertext.
 */

/** Longest edge after resizing; enough to look sharp full screen on most displays. */
export const MAX_EDGE = 2560;
/** Smaller images are sent as they are unless they need converting (e.g. HEIC). */
const SMALL_ENOUGH = 1024 * 1024;
const QUALITY = 0.85;

/** Formats the server and other browsers show inline; anything else gets converted. */
const SHOWN_INLINE = /^image\/(png|jpeg|webp|gif)$/;
/** Images we try to shrink. GIFs are left alone (re-encoding loses the animation). */
const SHRINKABLE = /^image\/(png|jpeg|webp|heic|heif|avif)$/;

export function fitWithin(width: number, height: number, max = MAX_EDGE) {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export function shouldShrink(
  file: { type: string; size: number },
  width: number,
  height: number,
): boolean {
  if (!SHRINKABLE.test(file.type)) return false;
  if (!SHOWN_INLINE.test(file.type)) return true; // HEIC & co: convert so everyone sees it
  return file.size > SMALL_ENOUGH || Math.max(width, height) > MAX_EDGE;
}

function renamed(name: string, ext: string) {
  const base = name.replace(/\.[^.]+$/, '') || 'image';
  return `${base}.${ext}`;
}

/** Returns a smaller copy of a large photo, or the file itself if it's fine (or not a photo). */
export async function shrinkImage(file: File): Promise<File> {
  if (!SHRINKABLE.test(file.type) || typeof createImageBitmap !== 'function') return file;
  let bitmap: ImageBitmap;
  try {
    // Applies EXIF orientation, so the result is upright.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file; // this browser can't decode it; send the original
  }
  try {
    if (!shouldShrink(file, bitmap.width, bitmap.height)) return file;
    const { width, height } = fitWithin(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);
    // Keep transparency for PNG/WebP (WebP is far smaller than PNG); photos become JPEG.
    const type =
      file.type === 'image/jpeg' || /hei[cf]|avif/.test(file.type) ? 'image/jpeg' : 'image/webp';
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, QUALITY));
    // Some browsers can't encode WebP and silently return PNG; keep whichever is smaller.
    if (!blob || (blob.size >= file.size && SHOWN_INLINE.test(file.type))) return file;
    const ext = blob.type === 'image/jpeg' ? 'jpg' : blob.type.split('/')[1]!;
    return new File([blob], renamed(file.name, ext), { type: blob.type });
  } finally {
    bitmap.close();
  }
}
